/**
 * iOS-only edge-swipe back gesture (custom JS implementation with live
 * snapshot-based slide animation).
 *
 * Why it exists
 * -------------
 * WKWebView's native `allowsBackForwardNavigationGestures` is disabled
 * in `AppDelegate.swift` and `Main.storyboard` because the gesture
 * traverses `WKBackForwardList`, which has documented sync bugs with SPA
 * `pushState/replaceState` — resulting in swipe-back landing on stale
 * entries from a previous session or a different category context
 * (confirmed on TestFlight 1.0 builds 7 and 8).
 *
 * This module reinstates the user-facing swipe-back feature but routes
 * it through the deterministic logical `appBack()` so every back path —
 * gesture, hardware back, visible button — pops the same logical stack.
 *
 * Animation model (build 40+)
 * ---------------------------
 * The earlier build only detected the gesture at touchend and called
 * `appBack()` instantly — the screen change was a jump, not a slide.
 * This build adds a live snapshot-based slide:
 *
 *   1. On every navigation forward, `appHistory.ts` calls
 *      `capturePageSnapshot(leavingUrl)` which clones `#root` into an
 *      in-memory store keyed by URL.
 *   2. On touchstart inside the edge band, we look up the snapshot for
 *      `appHistoryPrevious()` and, if found, render it as a fixed-position
 *      layer underneath the live `#root`. We then translate `#root`
 *      proportionally to finger motion, with a small parallax on the
 *      snapshot — matching the iOS native push-pop transition.
 *   3. On touchend: if the drag passed the commit threshold (50 % of the
 *      viewport width OR a fast flick), we animate `#root` fully out,
 *      then call `appBack()` and remove the snapshot. Otherwise we
 *      animate `#root` back to 0 and remove the snapshot — the screen
 *      stays where it was.
 *
 * If no snapshot exists for the previous URL (deep-link, fresh launch,
 * LRU eviction), we silently fall back to the legacy touchend-only
 * behaviour: detect the qualifying gesture at touchend and call
 * `appBack()` directly with no slide. This preserves user expectation
 * — they still get to go back, just without the polish.
 *
 * Gesture model
 * -------------
 *   Start zone — EITHER edge (44 px band):
 *     RTL (Arabic, default): right edge → start.x >= vw - EDGE_BAND_PX
 *     LTR (English / Turkish / Chinese): left edge → start.x <= EDGE_BAND_PX
 *   Back direction:
 *     RTL: dx ≤ -SWIPE_DISTANCE_PX  (rightward-start, move left)
 *     LTR: dx ≥ +SWIPE_DISTANCE_PX  (leftward-start, move right)
 *   Vertical drift: |dy| ≤ SWIPE_VERTICAL_LIMIT_PX  AND  |dx| > |dy|
 *   Duration: ≤ SWIPE_MAX_DURATION_MS
 *
 * Skip conditions
 * ---------------
 *   - touch started on a form field
 *   - `data-no-edge-swipe` ancestor (opt-out for custom carousels)
 *   - Radix dialog/alert open (data-state="open")
 *   - inside a genuinely scrollable horizontal container
 *   - 350 ms debounce on success
 */

import { Capacitor } from "@capacitor/core";
import { appBack, appHistoryPrevious } from "./appHistory";
import { getPageSnapshot } from "./pageSnapshot";

const EDGE_BAND_PX = 44;
const SWIPE_DISTANCE_PX = 40;
const SWIPE_VERTICAL_LIMIT_PX = 60;
const VERTICAL_CANCEL_PX = 12;
const SWIPE_MAX_DURATION_MS = 700;
const BACK_DEBOUNCE_MS = 350;
const LIVE_START_PX = 12;        // distance before the live slide kicks in
const SLIDE_DURATION_MS = 220;   // commit / cancel animation length
const COMMIT_PROGRESS = 0.5;     // fraction of viewport width = commit
const COMMIT_VELOCITY = 0.6;     // px / ms — fast flick also commits
const PARALLAX_FACTOR = 0.3;     // previous-page offset, native-iOS-style
const DIM_MAX = 0.18;            // dim overlay opacity at progress = 0

const IS_NATIVE_DIAG =
  typeof window !== "undefined" &&
  typeof (window as any).Capacitor !== "undefined" &&
  (window as any).Capacitor?.isNativePlatform?.() === true;

function diag(...args: unknown[]): void {
  if (IS_NATIVE_DIAG) {
    // eslint-disable-next-line no-console
    console.log("[EdgeSwipe]", ...args);
  }
}

interface TouchStart {
  x: number;
  y: number;
  t: number;
  cancelled: boolean;
  isBackEdge: boolean;   // started in the back-edge band for current dir
  rtl: boolean;
}

interface LiveSwipe {
  startX: number;
  startT: number;
  vw: number;
  rtl: boolean;
  root: HTMLElement;
  layer: HTMLDivElement;
  cloneEl: HTMLElement;
  dim: HTMLDivElement;
  consumedUrl: string;
  lastX: number;
  lastT: number;
  velocity: number;
}

function isRTL(): boolean {
  if (typeof document === "undefined") return false;
  const dir = document.documentElement?.dir || document.body?.dir;
  return dir === "rtl";
}

function isOnFormField(target: Element | null): boolean {
  if (!target || typeof target.closest !== "function") return false;
  return !!target.closest(
    'input, textarea, select, [contenteditable="true"], [contenteditable=""]',
  );
}

function isOptedOut(target: Element | null): boolean {
  if (!target || typeof target.closest !== "function") return false;
  return !!target.closest("[data-no-edge-swipe]");
}

function isInsideHorizontalScroll(target: Element | null): boolean {
  let el: Element | null = target;
  let depth = 0;
  while (el && el !== document.documentElement && depth < 10) {
    try {
      const cs = window.getComputedStyle(el);
      const ox = cs.overflowX;
      if (ox === "auto" || ox === "scroll") {
        const he = el as HTMLElement;
        if (he.scrollWidth > he.clientWidth + 2) return true;
      }
    } catch {
      /* defensive */
    }
    el = el.parentElement;
    depth++;
  }
  return false;
}

function isDialogOpen(): boolean {
  try {
    return !!document.querySelector(
      '[role="dialog"][data-state="open"], ' +
      '[role="alertdialog"][data-state="open"], ' +
      '[data-radix-scroll-lock="true"]',
    );
  } catch {
    return false;
  }
}

const SWIPE_INSTALLED = Symbol.for("iraqsooq.iosEdgeSwipe.v3.installed");

export function setupIosEdgeSwipe(): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  if (!Capacitor.isNativePlatform()) return;
  if (Capacitor.getPlatform() !== "ios") return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w[SWIPE_INSTALLED]) {
    diag("already installed — skip");
    return;
  }
  w[SWIPE_INSTALLED] = true;

  diag(
    "installed v3 — edge band:", EDGE_BAND_PX,
    "px | commit progress:", COMMIT_PROGRESS,
    "| RTL:", isRTL(),
    "| innerWidth:", window.innerWidth,
  );

  let touchStart: TouchStart | null = null;
  let live: LiveSwipe | null = null;
  let lastBackTs = 0;

  // ── Helpers for the live slide ────────────────────────────────────────

  function tryStartLive(start: TouchStart): boolean {
    const prevUrl = appHistoryPrevious();
    if (!prevUrl) {
      diag("live skipped: no previous logical entry");
      return false;
    }
    const snap = getPageSnapshot(prevUrl);
    if (!snap) {
      diag("live skipped: no snapshot for", prevUrl);
      return false;
    }
    const root = document.getElementById("root");
    if (!root) {
      diag("live skipped: #root not found");
      return false;
    }
    let layer: HTMLDivElement | null = null;
    let cloneEl: HTMLElement | null = null;
    let dim: HTMLDivElement | null = null;
    try {
      layer = document.createElement("div");
      layer.setAttribute("data-iraqsooq-swipe-layer", "true");
      layer.style.cssText = [
        "position:fixed",
        "inset:0",
        "z-index:0",
        "overflow:hidden",
        "pointer-events:none",
        "contain:strict",
        "background:var(--background, #ffffff)",
      ].join(";");
      cloneEl = snap.cloneNode(true) as HTMLElement;
      cloneEl.style.cssText += [
        ";position:absolute",
        "inset:0",
        "will-change:transform",
        `transform:translate3d(${(start.rtl ? 1 : -1) * PARALLAX_FACTOR * window.innerWidth}px, 0, 0)`,
      ].join(";");
      // Snapshot must never receive pointer events.
      cloneEl.style.pointerEvents = "none";
      layer.appendChild(cloneEl);
      dim = document.createElement("div");
      dim.style.cssText = [
        "position:absolute",
        "inset:0",
        "background:#000",
        `opacity:${DIM_MAX}`,
        "will-change:opacity",
        "pointer-events:none",
      ].join(";");
      layer.appendChild(dim);
      document.body.insertBefore(layer, document.body.firstChild);
      // Prep the live shell. We deliberately do NOT change position so
      // any `position:fixed` descendants (headers, bottom-nav) keep their
      // anchoring; we only add a transform, which still slides them.
      root.style.willChange = "transform";
      root.style.transition = "";
      root.style.transform = "translate3d(0,0,0)";
      // Mark body so any code that wants to react can detect a swipe in
      // progress (and so we can tidy up if torn down externally).
      document.documentElement.setAttribute("data-iraqsooq-swiping", "true");
    } catch (err) {
      diag("live setup failed:", err);
      try { if (layer && layer.parentNode) layer.parentNode.removeChild(layer); } catch { /* ignore */ }
      return false;
    }
    live = {
      startX: start.x,
      startT: start.t,
      vw: window.innerWidth,
      rtl: start.rtl,
      root,
      layer,
      cloneEl: cloneEl!,
      dim: dim!,
      consumedUrl: prevUrl,
      lastX: start.x,
      lastT: start.t,
      velocity: 0,
    };
    diag("live started, prev=", prevUrl);
    return true;
  }

  function updateLive(curX: number): void {
    if (!live) return;
    const { startX, vw, rtl, root, cloneEl, dim } = live;
    const dxRaw = curX - startX;
    // Convert to signed progress where positive = swiping toward back.
    const signed = rtl ? -dxRaw : dxRaw;
    const progress = Math.max(0, Math.min(1, signed / vw));
    const liveOffset = (rtl ? -1 : 1) * progress * vw;
    root.style.transform = `translate3d(${liveOffset.toFixed(2)}px, 0, 0)`;
    const cloneStart = (rtl ? 1 : -1) * PARALLAX_FACTOR * vw;
    const cloneNow = cloneStart - cloneStart * progress;
    cloneEl.style.transform = `translate3d(${cloneNow.toFixed(2)}px, 0, 0)`;
    dim.style.opacity = String((DIM_MAX * (1 - progress)).toFixed(3));
    // Sample velocity over the last touchmove for the fast-flick check.
    const now = performance.now();
    const dt = Math.max(1, now - live.lastT);
    const dxLast = curX - live.lastX;
    const vNow = (rtl ? -dxLast : dxLast) / dt; // px / ms toward back
    // Light smoothing so a single jittery sample cannot decide commit.
    live.velocity = live.velocity * 0.6 + vNow * 0.4;
    live.lastX = curX;
    live.lastT = now;
  }

  function endLive(curX: number, durationMs: number): boolean {
    if (!live) return false;
    const { startX, vw, rtl, root, layer, cloneEl, dim, consumedUrl, velocity } = live;
    const dxRaw = curX - startX;
    const signed = rtl ? -dxRaw : dxRaw;
    const progress = signed / vw;
    const shouldCommit = progress > COMMIT_PROGRESS || velocity > COMMIT_VELOCITY;
    diag("live ending — progress:", progress.toFixed(2), "vel:", velocity.toFixed(3), "commit:", shouldCommit);
    const transition = `transform ${SLIDE_DURATION_MS}ms ease-out`;
    const opacityTransition = `opacity ${SLIDE_DURATION_MS}ms ease-out`;
    root.style.transition = transition;
    cloneEl.style.transition = transition;
    dim.style.transition = opacityTransition;
    // Schedule the cleanup just past the animation end so the user sees
    // the final frame before the layer disappears.
    const cleanupDelay = SLIDE_DURATION_MS + 30;
    if (shouldCommit) {
      root.style.transform = `translate3d(${((rtl ? -1 : 1) * vw).toFixed(2)}px, 0, 0)`;
      cloneEl.style.transform = "translate3d(0,0,0)";
      dim.style.opacity = "0";
      // Suppress the duration variable (currently informational only) —
      // could later feed a velocity-scaled animation length.
      void durationMs;
      window.setTimeout(() => {
        teardownLive(true, consumedUrl);
      }, cleanupDelay);
    } else {
      root.style.transform = "translate3d(0,0,0)";
      const cloneStart = (rtl ? 1 : -1) * PARALLAX_FACTOR * vw;
      cloneEl.style.transform = `translate3d(${cloneStart.toFixed(2)}px, 0, 0)`;
      dim.style.opacity = String(DIM_MAX);
      window.setTimeout(() => {
        teardownLive(false, consumedUrl);
      }, cleanupDelay);
    }
    return shouldCommit;
  }

  function teardownLive(committed: boolean, _consumedUrl: string): void {
    void _consumedUrl;
    if (!live) return;
    const { root, layer } = live;
    live = null;
    try {
      root.style.transition = "";
      root.style.transform = "";
      root.style.willChange = "";
    } catch {
      /* ignore */
    }
    try {
      if (layer && layer.parentNode) layer.parentNode.removeChild(layer);
    } catch {
      /* ignore */
    }
    try {
      document.documentElement.removeAttribute("data-iraqsooq-swiping");
    } catch {
      /* ignore */
    }
    if (committed) {
      const now = Date.now();
      if (now - lastBackTs < BACK_DEBOUNCE_MS) {
        diag("commit debounced");
        return;
      }
      lastBackTs = now;
      appBack();
    }
  }

  // ── Touch handlers ────────────────────────────────────────────────────

  const onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length !== 1) {
      touchStart = null;
      return;
    }
    const touch = e.touches[0];
    if (!touch) { touchStart = null; return; }

    if (isOnFormField(e.target as Element | null)) {
      touchStart = null;
      return;
    }

    const rtl = isRTL();
    const vw = window.innerWidth;
    const startedNearRightEdge = vw - touch.clientX <= EDGE_BAND_PX;
    const startedNearLeftEdge = touch.clientX <= EDGE_BAND_PX;
    const isBackEdge =
      (rtl && startedNearRightEdge) || (!rtl && startedNearLeftEdge);

    touchStart = {
      x: touch.clientX,
      y: touch.clientY,
      t: Date.now(),
      cancelled: false,
      isBackEdge,
      rtl,
    };
  };

  const onTouchMove = (e: TouchEvent): void => {
    if (!touchStart || touchStart.cancelled) return;
    if (e.touches.length !== 1) {
      touchStart = null;
      if (live) endLive(live.lastX, Date.now() - live.startT);
      return;
    }
    const touch = e.touches[0];
    if (!touch) return;
    const dy = touch.clientY - touchStart.y;
    const dxRaw = touch.clientX - touchStart.x;
    const signed = touchStart.rtl ? -dxRaw : dxRaw;

    if (!live) {
      // Pre-live: cheap scroll-intent cancel.
      if (Math.abs(dy) > VERTICAL_CANCEL_PX && Math.abs(dy) > Math.abs(dxRaw)) {
        touchStart.cancelled = true;
        return;
      }
      // If we are on the back edge AND have moved enough toward back AND
      // the gesture is unmistakeably horizontal, try to start the live
      // slide. Exclusion checks run here (target-related ones only) so
      // we don't tear off the live layer mid-gesture.
      if (
        touchStart.isBackEdge &&
        signed > LIVE_START_PX &&
        Math.abs(dxRaw) > Math.abs(dy) &&
        !isDialogOpen() &&
        !isOptedOut(e.target as Element | null) &&
        !isInsideHorizontalScroll(e.target as Element | null)
      ) {
        if (tryStartLive(touchStart)) {
          // Apply the first frame immediately so the slide tracks the finger.
          updateLive(touch.clientX);
          // Prevent the browser from doing native horizontal scrolling
          // while we are driving the transform manually.
          try { e.preventDefault(); } catch { /* passive listener — best-effort */ }
        }
      }
      return;
    }

    // Live mode: update the transform every move.
    try { e.preventDefault(); } catch { /* passive — best-effort */ }
    updateLive(touch.clientX);
  };

  const onTouchEnd = (e: TouchEvent): void => {
    const start = touchStart;
    touchStart = null;

    const touch = e.changedTouches[0];
    if (!touch) {
      if (live) endLive(live.lastX, Date.now() - live.startT);
      return;
    }

    // Live mode: just decide commit vs cancel.
    if (live) {
      const dt = Date.now() - live.startT;
      endLive(touch.clientX, dt);
      return;
    }

    if (!start || start.cancelled) return;

    // Fallback path (no snapshot was available, or live never engaged):
    // mirror the v2 legacy behaviour and call appBack() on a qualifying
    // discrete gesture.
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const dt = Date.now() - start.t;

    if (dt > SWIPE_MAX_DURATION_MS) return;
    if (Math.abs(dy) > SWIPE_VERTICAL_LIMIT_PX) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    if (Math.abs(dx) < SWIPE_DISTANCE_PX) return;

    const isRTLBack = start.rtl && start.isBackEdge && dx < 0;
    const isLTRBack = !start.rtl && start.isBackEdge && dx > 0;
    if (!isRTLBack && !isLTRBack) return;

    const target = e.target as Element | null;
    if (isOptedOut(target)) return;
    if (isDialogOpen()) return;
    if (isInsideHorizontalScroll(target)) return;

    const now = Date.now();
    if (now - lastBackTs < BACK_DEBOUNCE_MS) return;
    lastBackTs = now;
    diag("FALLBACK back (no snapshot) →", { dx: Math.round(dx), dt });
    appBack();
  };

  const onTouchCancel = (): void => {
    touchStart = null;
    if (live) endLive(live.lastX, Date.now() - live.startT);
  };

  // Capture phase — fires before any component or scroll handler.
  // touchmove is registered with passive:false so that preventDefault()
  // works once the live slide engages. We never preventDefault before
  // engagement, so vertical scrolling stays as smooth as before.
  document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
  document.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
  document.addEventListener("touchend", onTouchEnd, { passive: true, capture: true });
  document.addEventListener("touchcancel", onTouchCancel, { passive: true, capture: true });
}
